/**
 * @file  src\services\wantAdService.js
 * @brief  This file is responsible for want ad services
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import * as endPoints from "../../constants/v2/endPoints";
import { getUserId, getLocalStorage } from "../../helpers/common";
import * as apiService from "../v2/apiService";
import { USER_INFO_KEY } from "../../constants/common";
import { isNumber } from "lodash";

export async function getWantAds(filterQuery) {
  return await apiService.apiCall(endPoints.GET_WANT_ADS, "GET", filterQuery);
}

export async function getWantAdsPublic(filterQuery) {
  return await apiService.apiCall(
    endPoints.GET_WANT_ADS_PUBLIC,
    "GET",
    filterQuery,
  );
}

export async function getPurchaseTypes() {
  return await apiService.apiCall(endPoints.GET_PURCHASE_TYPE, "GET", {});
}

export async function getFinanceType() {
  return await apiService.apiCall(endPoints.GET_FINANCE_TYPE, "GET", {});
}

export async function getRadiusList() {
  return await apiService.apiCall(endPoints.RADIUS_LIST_API, "GET", {});
}

export async function saveMemberWantAd(requestPayload) {
  return await apiService.apiCall(
    endPoints.SAVE_MEMBER_WANT_AD_V2,
    "POST",
    requestPayload,
  );
}

// AI nice-to-have auto-tagging (backend fails soft: gates/errors answer
// { suggestions: [] }, so callers never need to handle a hard failure).
export async function suggestNiceToHaves(requestPayload) {
  return await apiService.apiCall(
    endPoints.SUGGEST_NICE_TO_HAVES_V2,
    "POST",
    requestPayload,
  );
}

// AI ranking for a want-ad's matches (fails soft the same way:
// { enabled: false, rankings: [] } when off, absent, or erroring).
export async function getAiRanking(wantAdId) {
  return await apiService.apiCall(
    endPoints.GET_AI_RANKING_V2 + `?wantAdId=${wantAdId}`,
    "GET",
    {},
  );
}

// Reverse direction: AI pairings for one listing across displayed want-ads.
export async function getAiRankingForListing({ mlsListingKey, inventoryId }) {
  const q = mlsListingKey
    ? `mlsListingKey=${encodeURIComponent(mlsListingKey)}`
    : `inventoryId=${inventoryId}`;
  return await apiService.apiCall(
    endPoints.GET_AI_RANKING_FOR_LISTING_V2 + `?${q}`,
    "GET",
    {},
  );
}

export async function saveBuyerListingWantAd(requestPayload) {
  return await apiService.apiCallWithoutToken(
    endPoints.SAVE_BUYER_LISTING_WANTAD_V2,
    "POST",
    requestPayload,
  );
}

export async function saveMemberWantAdstrengthenData(requestPayload) {
  return await apiService.apiCall(
    endPoints.SAVE_MEMBER_WANT_AD_STRENGTHN,
    "POST",
    requestPayload,
  );
}

export async function saveBuyerListStrengthenWantAd(requestPayload) {
  return await apiService.apiCallWithoutToken(
    endPoints.SAVE_BUYER_LIST_STRENGTHEN_WANTAD_V2,
    "POST",
    requestPayload,
  );
}

export async function getWantAdById(slug, userId = null) {
  const isWantAdId = /^[0-9]+$/.test(slug);
  return await apiService.apiCall(
    endPoints.GET_WANT_ID_BY_ID + `?slug=${slug}`,
    "GET",
    {},
  );
}

export async function syndicateWantAdListing(wantAdId) {
  return await apiService.apiCall(
    endPoints.SYNDICATE_WANT_AD_TO_GITCHA + `?wantAdId=${wantAdId}`,
    "POST",
    {},
  );
}

export async function unsyndicateWantAdListing(wantAdId) {
  const response = await apiService.apiCall(
    endPoints.UNSYNDICATE_WANT_AD_FROM_GITCHA + `?wantAdId=${wantAdId}`,
    "POST",
    {},
  );
  // A refused request comes back as a normal response; throw so the caller's
  // onError surfaces it instead of the confirm dialog closing on nothing.
  if (response?.errorCode !== 0 || !response?.result) {
    throw new Error(response?.errorMessage || "Unsyndicate failed");
  }
  return response;
}

export async function getHasPublicSyndication() {
  const userId = getUserId();
  return await apiService.apiCall(
    endPoints.HAS_PUBLIC_SYNDICATION + `?userId=${userId}`,
    "GET",
    {},
  );
}

export async function getProfileSlug() {
  return await apiService.apiCall(endPoints.GET_PROFILE_SLUG, "GET", {});
}

export async function getWantAdByIdPublic(slug, userId = null) {
  return await apiService.apiCall(
    endPoints.GET_WANT_ID_BY_ID_PUBLIC + `?slug=${slug}`,
    "GET",
    {},
  );
}

export async function getBuyerListWantAdById(wantAdId) {
  return await apiService.apiCallWithoutToken(
    endPoints.GET_BUYERLIST_WANTAD_BY_ID + `?id=${wantAdId}`,
    "GET",
    {},
  );
}

export async function getWantAdDetailsById(wantAdId, userId = null) {
  const isWantAdId = /^[0-9]+$/.test(wantAdId);
  return await apiService.apiCallWithoutToken(
    endPoints.GET_WANT_AD_DETAILS_BY_ID +
    `?${isWantAdId ? "id" : "slug"}=${wantAdId}`,
    "GET",
    {},
  );
}

export async function getMyDraft(userId) {
  return await apiService.apiCall(endPoints.GET_MY_DRAFT, "GET", {});
}

export async function getCreditScores() {
  return await apiService.apiCall(endPoints.GET_CREDIT_SCORE, "GET", {});
}

export async function getMyWantAds(requestPayload) {
  return await apiService.apiCall(
    endPoints.GET_MY_WANT_ADS,
    "GET",
    requestPayload,
  );
}

export async function setWantAdClient(wantAdId, requestPayload) {
  return await apiService.apiCall(
    endPoints.SET_WANTAD_CLIENT.replace(":wantAdId", wantAdId),
    "PATCH",
    requestPayload,
  );
}

export async function getAgentsList(requestPayload) {
  return await apiService.apiCall(
    endPoints.GET_AGENTS_LIST,
    "GET",
    requestPayload,
  );
}

export async function deleteWantAd(wantAdId) {
  let userId = getUserId();
  return await apiService.apiCall(
    endPoints.DELETE_WANTAD + `?wantAdId=${wantAdId}`,
    "PUT",
    {},
  );
}

export async function updateWantAdStatus(wantAdId, status, closureData) {
  return await apiService.apiCall(
    endPoints.UPDATE_WANTAD_STATUS + `?wantAdId=${wantAdId}`,
    "PUT",
    { status: status, closureData },
  );
}

export async function archiveWantAd(wantAdId) {
  return await apiService.apiCall(
    endPoints.ARCHIVE_WANTAD + `?wantAdId=${wantAdId}`,
    "PUT",
    {},
  );
}

export async function unArchiveWantAd(wantAdId) {
  return await apiService.apiCall(
    endPoints.UNARCHIVE_WANTAD + `?wantAdId=${wantAdId}`,
    "PUT",
    {},
  );
}

export async function favouriteWantAd(requestPayload) {
  return await apiService.apiCall(
    endPoints.FAVORITE_WANTAD,
    "POST",
    requestPayload,
  );
}

export async function unFavouriteWantAd(requestPayload) {
  return await apiService.apiCall(
    endPoints.UNFAVORITE_WANTAD,
    "DELETE",
    requestPayload,
  );
}

export async function getFavouriteWantAdList(requestPayload) {
  return await apiService.apiCall(
    endPoints.FAVORITE_WANTAD_LIST,
    "GET",
    requestPayload,
  );
}

export async function getFavClientList(requestPayload) {
  return await apiService.apiCall(
    endPoints.FAVOURITE_CLIENTS,
    "GET",
    requestPayload,
  );
}

export async function unfavouriteAllWantAd(requestPayload) {
  return await apiService.apiCall(
    endPoints.UNFAVORITE_ALL_WANTAD,
    "DELETE",
    requestPayload,
  );
}

export async function getGitachGenieMatchedWantAds(filterQuery) {
  return await apiService.apiCall(
    endPoints.GET_GITCHA_GENIE_MATCHED_WANTADS,
    "GET",
    filterQuery,
  );
}

export async function reportWantAd(requestPayload) {
  return await apiService.apiCall(
    endPoints.REPORT_WANT_AD,
    "POST",
    requestPayload,
  );
}

export async function getLotSizeList() {
  return await apiService.apiCall(
    endPoints.WANT_AD_LOT_SIZE_LIST_API,
    "GET",
    {},
  );
}

export async function getSquareFeetList() {
  return await apiService.apiCallWithoutToken(
    endPoints.WANT_AD_SQUARE_FEET_LIST_API,
    "GET",
    {},
  );
}

export async function getUserInventoryCount() {
  const userDetails = getLocalStorage(USER_INFO_KEY);
  const userId = _.get(userDetails, "userInfo.userId", null);
  return await apiService.apiCall(
    endPoints.GET_USER_INVENTORY_COUNT,
    "GET",
    {},
  );
}

export async function shareWantAd(payload) {
  return await apiService.apiCall(
    endPoints.SHARE_WANTAD +
    `?wantAdId=${payload?.wantAdId}&fileName=${payload?.fileName}`,
    "GET",
    {},
  );
}

/**
 * To get the share wantAd social card url
 */

export async function shareWantAdSocial(payload) {
  return await apiService.apiCall(
    endPoints.SHARE_WANTAD_SOCIAL +
    `?wantAdId=${payload?.wantAdId}&platform=${payload?.platform}&origin=${encodeURIComponent(window.location.origin)}`,
    "GET",
    {},
  );
}

export async function getBoundaryCoordinates(payload) {
  return await apiService.apiCall(
    endPoints.GET_BOUNDARY_COORDINATES + `?zipcode=${payload?.zipCode}`,
    "GET",
    {},
  );
}

export async function getShareWantAdDetails(payload) {
  return await apiService.apiCall(
    endPoints.GET_SHARE_WANTAD_DETAILS + `/${payload?.id}`,
    "GET",
    {},
  );
}
